<template>
  <AppSection :class="{ 'status-page': view == 'status' }">
    <AppContainer>
      <AppThirdLayout>
        <div>
          <MobileOnly>
            <AppHeaderWithImg 
              :text="$tt('page.how_it_works')"
              src="/images/about/docement-with-check-mark-icon.jpg"
              :alt="$tt('page.how_it_works')"
              class="header-with-img"
              row
            />
          </MobileOnly>
          <DesktopOnly>
            <h2 v-if="!titleless">{{ $tt('page.how_it_works') }}</h2>
            <figure class="pt-6">
              <AppImg :src="img.src" :alt="$t(img.alt!)" />
            </figure>
          </DesktopOnly>
        </div>
        <div class="steps-container flex col justify-between gap-7 items-start mt-2 pb-2">
          <ol class="steps flex col gap-7">
            <li v-for="({ title, icon }, i) in STEPS" class="step flex gap-3 ms-0">
              <DesktopOnly>
                <AppIcon :icon color="tertiary" size="x-large" start />
              </DesktopOnly>
              <div>
                <h3 class="mb-3 flex items-center justify-between">
                  {{ $tt(title) }}
                  <MobileOnly>
                    <AppIcon :icon color="tertiary" size="x-large" start />
                  </MobileOnly>
                </h3>
                <p v-html="$t(`page.steps.${i + 1}_body_${tSuffix}`)" />
              </div>
            </li>
          </ol>
          <div class="btns-container flex gap-5 wrap mb-6" :class="{ 'multiple': example }">
            <AppBtn v-if="example" size="x-large" @click="modalOpen = true">
              {{ $tt(`visa_example_${countryKey!}`) }}
              <AppIcon icon="mdi:certificate-outline" end />
            </AppBtn>
            <AppBtn size="x-large" primary @click="navigate">
              {{ $tt(primaryCtaText) }}
              <ChevronMarker dir="next" class="ms-2" />
            </AppBtn>
          </div>
          <MobileOnly>
            <figure>
              <AppImg :src="img.src" :alt="$t(img.alt!)" />
            </figure>
          </MobileOnly>
        </div>
      </AppThirdLayout>
    </AppContainer>

    <VisaExampleModal v-if="example" v-model="modalOpen" :example />
  </AppSection>
</template>

<script setup lang="ts">
import { countryKeyInjectionKey } from '~/assets/injection-keys';
import type { Image } from '~/types';
import { getHashId } from '~/helpers/routing';
import type { I18nKey } from '~/helpers/locale';

type Step = {
  title: I18nKey;
  icon: string;
}

const countryKey = inject(countryKeyInjectionKey, null);
const props = defineProps<{
  example?: Image;
  view?: 'home' | 'status';
  titleless?: boolean;
}>();

const tSuffix = countryKey || props.view!;
const STEPS: Step[] = [
  {
    title: `page.steps.1_title_${tSuffix}`,
    icon: props.view == 'status' ? 'ri:24-hours-fill' : 'ph:number-circle-one-duotone'
  }, 
  {
    title: `page.steps.2_title_${tSuffix}`,
    icon: props.view == 'status' ? 'mdi:form-outline' : 'ph:number-circle-two-duotone'
  }, 
  {
    title: `page.steps.3_title_${tSuffix}`,
    icon: props.view == 'status' ? 'icon-park-outline:doc-search-two' : 'ph:number-circle-three-duotone'
  }
];

const img: Image = {
  src: '/images/woman-chilling-in-armchair.png',
  alt: 'page.steps.img_alt'
};

const modalOpen = ref(false);
const router = useRouter();
const { ctaClickBus } = useEmitter();

const primaryCtaText = countryKey ? 'page.steps.cta_country_page' : 'page.steps.cta_home';
function navigate() {
  if (props.view) {
    router.push(`/${getHashId('countries_overview')}`);
  } else {
    ctaClickBus.emit();
  }
}
</script>

<style lang="scss" scoped>
@media (min-width: 851px) {
  .btns-container {
    width: 100%;
    padding-inline-start: 54px;
    &.multiple button {
      flex: 1;
    }
  }
}

@media (max-width: 850px) {
  .header-with-img {
    margin-inline-start: calc(-1 * var(--space-2));
  }
  .steps-container {
    align-items: center;
  }
  .steps {
    gap: var(--space-5);

    .step {
      flex-direction: column;
      gap: 0;
      margin: 0;
    }
  }

  h3 {
    font-size: 20px;
  }

  .layout {
    position: relative;
    gap: var(--space-5) !important;
  }
  figure {
    margin: calc(-1 * var(--space-3)) auto;
    &>img {
      max-width: 360px;
      width: 100%;
    }
  }
  .btns-container {
    flex-direction: column;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    margin-bottom: 0 !important;
    button {
      width: 100% !important;
    }
  }

  .status-page {
    padding-top: var(--space-5) !important;
  }
}
</style>